<style scoped>
.transcripts-queue .queue-filters {
	display: flex;
	gap: 0.5rem;
	align-items: center;
	flex-wrap: wrap;
	font-size: 0.875rem;
	font-weight: 400;
}

.transcripts-queue .queue-filters input[type="text"],
.transcripts-queue .queue-filters input[type="date"],
.transcripts-queue .queue-filters select {
	padding: 0.25rem 0.5rem;
	border: 1px solid #ddd;
	border-radius: 4px;
	font-size: 0.875rem;
}

.transcripts-queue .queue-filters .search {
	flex: 1 1 100%;
}

.transcripts-queue .avatar-col {
	width: 2.25rem;
	padding-right: 0;
}

/* Technician avatar in a queue row — same idiom as ExportsPanel's row avatar:
   the image when the user has one, their initials otherwise. */
.transcripts-queue .technician-avatar {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 24px;
	height: 24px;
	border-radius: 50%;
	object-fit: cover;
	background: #e9ecef;
	color: #6c757d;
	font-size: 0.625rem;
	font-weight: 700;
	vertical-align: middle;
}

.transcripts-queue table th {
	font-weight: 600;
	white-space: nowrap;
}

.transcripts-queue table td {
	white-space: nowrap;
}

.transcripts-queue tbody tr {
	cursor: pointer;
}

.transcripts-queue tbody tr.selected {
	background: #cfe2ff;
}

.transcripts-queue .queue-footer {
	font-size: 0.875rem;
	color: #666;
	font-weight: 500;
}

.queue-error {
	color: #b02a37;
	font-size: 0.8125rem;
}
</style>

<template>
	<div class="transcripts-queue">
		<div class="queue-filters mb-2">
			<input
				v-model="searchTerm"
				class="search"
				type="text"
				placeholder="Search id, transcription, technician…"
			/>
			<select v-model="technicianFilter">
				<option value="">Technician: all</option>
				<option v-for="tech in technicians" :key="tech" :value="tech">
					{{ tech }}
				</option>
			</select>
			<select v-model="statusFilter">
				<option value="">Status: all</option>
				<option v-for="status in statuses" :key="status" :value="status">
					{{ status }}
				</option>
			</select>
			<input v-model="dateFilter" type="date" title="Recording date" />
			<button
				v-if="activeFilterCount > 0"
				class="btn btn-sm btn-outline-secondary"
				@click="clearFilters"
			>
				Clear ({{ activeFilterCount }})
			</button>
		</div>
		<div class="table-responsive">
			<table class="table table-sm table-hover mb-0">
				<thead>
					<tr>
						<th class="avatar-col" title="Technician"></th>
						<th title="Newest first">ID</th>
						<th title="Recording start">Started</th>
						<th title="Recording length">Length</th>
					</tr>
				</thead>
				<tbody>
					<tr
						v-for="row in rows"
						:key="row.note.id"
						:class="{ selected: row.note.id === selectedId }"
						@click="select(row.note)"
					>
						<td class="avatar-col">
							<span :title="row.note.technician">
								<img
									v-if="row.user?.avatar"
									:src="row.user.avatar"
									:alt="row.note.technician"
									class="technician-avatar"
								/>
								<span v-else class="technician-avatar">
									{{ row.user?.initials || "?" }}
								</span>
							</span>
						</td>
						<td>#{{ row.note.id }}</td>
						<td>
							<TimeRelativeWithTooltip :time="row.note.timestamp" />
						</td>
						<td>
							{{ formatTranscriptDuration(row.note.durationSeconds) }}
						</td>
					</tr>
					<tr v-if="loading && transcripts.length === 0">
						<td colspan="4" class="text-center text-muted py-3">Loading…</td>
					</tr>
					<tr v-if="!loading && transcripts.length === 0">
						<td colspan="4" class="text-center text-muted py-3">
							{{
								activeFilterCount > 0
									? "No transcripts match the current filters"
									: "No transcripts"
							}}
						</td>
					</tr>
				</tbody>
			</table>
		</div>
		<div
			class="queue-footer mt-2 d-flex justify-content-between align-items-center"
		>
			<span> {{ transcripts.length }} transcripts · newest first </span>
			<span v-if="loading" class="text-muted">Refreshing…</span>
			<button
				v-else-if="hasMore"
				class="btn btn-sm btn-outline-secondary"
				@click="emit('load-more')"
			>
				Load more
			</button>
		</div>
		<div v-if="error" class="queue-error mt-1">{{ error }}</div>
	</div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";

import type {
	MapReviewQueryBody,
	MapReviewQueryBodyFilter,
	TranscriptNote,
	User,
} from "@/type/api";
import type { TranscriptReviewStatus } from "@/type/api";
import TimeRelativeWithTooltip from "@/components/TimeRelativeWithTooltip.vue";
import { formatTranscriptDuration } from "@/lib/transcript";
import { useUserStore } from "@/store/user";

interface Props {
	/** Queue rows as delivered by the server (newest first, paged). */
	transcripts: TranscriptNote[];
	/** A queue fetch is in flight. */
	loading: boolean;
	selectedId?: string;
	/** Another page is available (the envelope still mints a cursor). */
	hasMore: boolean;
	/** Last queue fetch failed; message shown under the table. */
	error: string | null;
}
interface Emits {
	"selection-change": [transcript: TranscriptNote | undefined];
	/** Filter fields changed (debounced) — the parent refetches page 1. The
	 * body is the generic QUERY narrowing the resource declares
	 * (doc/in-progress/query.md §11 Phase 4). */
	"filters-change": [body: MapReviewQueryBody];
	/** User asked for the next page. */
	"load-more": [];
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const userStore = useUserStore();

// A note carries only its creator's display name, so the user list is pulled
// once (the store caches it and refreshes on its own SSE events) and the
// avatars are matched here rather than on the note endpoint.
const users = ref<User[]>([]);
onMounted(() => {
	userStore
		.withAll()
		.then((all) => {
			users.value = all;
		})
		.catch(() => {
			// Avatars are decorative — a failed lookup just leaves them blank.
		});
});

const usersByName = computed(
	() => new Map(users.value.map((u) => [u.display_name, u])),
);

/** A queue row joined with its technician's user record (for the avatar). */
interface QueueRow {
	note: TranscriptNote;
	user?: User;
}

const rows = computed<QueueRow[]>(() =>
	props.transcripts.map((note) => ({
		note,
		user: usersByName.value.get(note.technician),
	})),
);

// Filter fields are LOCAL state (the input is what the user typed); changes are
// reported to the parent as the resource's own clauses, which applies them
// server-side. Option lists for the free-form technician field come from the
// loaded page (the full corpus is server-paged, so this is a best-effort
// vocabulary).
//
// There is no route control: nothing records a note's route, so the resource
// declares no such property (`/api/transcript`'s descriptor says why) and the
// dropdown the legacy queue offered could never be populated — every note it
// listed carried an empty route.
const searchTerm = ref("");
const technicianFilter = ref("");
const statusFilter = ref<"" | TranscriptReviewStatus>("");
const dateFilter = ref("");

const statuses: TranscriptReviewStatus[] = [
	"Recorded",
	"In Review",
	"Awaiting Clarification",
	"Superseded",
	"Closed",
];

const technicians = computed<string[]>(() =>
	Array.from(
		new Set(
			props.transcripts.map((t) => t.technician).filter((v) => v.length > 0),
		),
	).sort(),
);

const activeFilterCount = computed(() => {
	let count = 0;
	if (searchTerm.value.trim()) count++;
	if (technicianFilter.value) count++;
	if (statusFilter.value) count++;
	if (dateFilter.value) count++;
	return count;
});

const clearFilters = () => {
	searchTerm.value = "";
	technicianFilter.value = "";
	statusFilter.value = "";
	dateFilter.value = "";
};

// Debounced report: search-as-you-type maps to one server round-trip per
// pause; the dropdowns report immediately.
let filterTimer: ReturnType<typeof setTimeout> | undefined;
watch([searchTerm, technicianFilter, statusFilter, dateFilter], () => {
	if (filterTimer) clearTimeout(filterTimer);
	filterTimer = setTimeout(emitFilters, 300);
});
onBeforeUnmount(() => {
	if (filterTimer) clearTimeout(filterTimer);
});

/**
 * The filter panel's state as the queue resource's own clauses. The free-text
 * box is the resource's computed `search` (the note id's digits, the
 * transcription and the creator's display name, OR-combined) and the dropdowns
 * are exact matches on the technician and the review status.
 *
 * The date input is a UTC day, which the generic grammar states as an `after`
 * bound on the day's first instant and a `before` bound on its last: the window
 * the legacy `?date=` parameter meant (created >= the day, < the next day).
 */
const emitFilters = () => {
	const filter: MapReviewQueryBodyFilter[] = [];
	const search = searchTerm.value.trim();
	if (search) {
		filter.push({ property: "search", operator: "contains", value: search });
	}
	if (technicianFilter.value) {
		filter.push({
			property: "technician",
			operator: "equal",
			value: technicianFilter.value,
		});
	}
	if (statusFilter.value) {
		filter.push({
			property: "status",
			operator: "equal",
			value: statusFilter.value,
		});
	}
	if (dateFilter.value) {
		filter.push({
			property: "created",
			operator: "after",
			value: `${dateFilter.value}T00:00:00Z`,
		});
		filter.push({
			property: "created",
			operator: "before",
			value: `${dateFilter.value}T23:59:59.999Z`,
		});
	}
	emit("filters-change", { filter });
};

const select = (transcript: TranscriptNote) => {
	// Clicking the open row again closes the detail panel.
	if (transcript.id === props.selectedId) {
		emit("selection-change", undefined);
		return;
	}
	emit("selection-change", transcript);
};
</script>
